import { useRef, useState } from "react";
import { get } from "../../utils/ApiRequest";
import Select from 'react-select';
import { compatApi } from "../../utils/Intercepter";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import toastConfig from "../../const/toastConfig";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";
import OrgSectionTabs from "../../components/OrgSectionTabs";
import useTokenStore from "../../stores/tokens";

export default function OrganizationJoin() {
  const { user } = useTokenStore();
  // 이미 소속/승인대기(leave_date IS NULL)인 조직 → my-orgs(orgUserList)에 존재. 재가입 대상(퇴사 조직)은 목록에 없음.
  const membershipOf = (orgCode) => {
    const m = (user?.orgUserList || []).find(o => o.organizationCode === orgCode);
    if (!m) return null;
    return m.isApproved ? 'joined' : 'pending';
  };

  const [optionList, setOptionList] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const searchTimerRef = useRef(null);

  const handleOrganizationChange = async (selectedOption) => {
    if (!selectedOption) return;

    const { organizationName, organizationCode } = selectedOption;

    // 이미 가입/승인대기 조직이면 가입 신청 없이 안내만
    if (selectedOption.membership) {
      await Swal.fire({
        title: '이미 신청/가입된 조직입니다.',
        text: selectedOption.membership === 'joined'
          ? '이미 소속되어 있는 조직입니다.'
          : '이미 가입 신청되어 승인 대기 중인 조직입니다.',
        icon: 'info',
        confirmButtonText: '확인',
        confirmButtonColor: PURPLE,
      });
      setSelectedOrganization(null);
      setInputValue('');
      setMenuIsOpen(false);
      return;
    }

    const result = await Swal.fire({
      title: '조직 가입 신청',
      html: `<p>${organizationName}(${organizationCode})에<br/>가입 신청하시겠습니까?</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '확인',
      cancelButtonText: '취소',
      confirmButtonColor: PURPLE,
      cancelButtonColor: '#6c757d'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await compatApi.post('/org/join', { organizationCode });
      if (response.ok) {
        toast.success('조직 가입 신청이 완료되었습니다.', toastConfig);
        setSelectedOrganization(null);
        setInputValue('');
        setMenuIsOpen(false);
        setTimeout(() => { window.location.href = '/'; }, 500);
      } else {
        toast.error('조직 가입에 실패했습니다. 다시 시도해주세요.', toastConfig);
      }
    } catch (error) {
      // 409: 이미 가입/승인 대기 중인 조직 → 서버 안내 메시지 노출
      const msg = error?.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : '조직 가입 중 오류가 발생했습니다.', toastConfig);
    }
  };

  const handleInputValueChange = (value) => {
    if (value.length > 100) return;
    setInputValue(value);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (value.length < 1) {
      setMenuIsOpen(false);
      setOptionList([]);
      return;
    }

    searchTimerRef.current = setTimeout(async () => {
      await searchOrganizations(value);
      setMenuIsOpen(true);
    }, 300);
  };

  const searchOrganizations = async (searchWord) => {
    if (!searchWord || searchWord.length < 1) {
      setOptionList([]);
      setIsSearching(false);
      return;
    }
    try {
      setIsSearching(true);
      const response = await get(`/org/find/organization/${searchWord}`);
      if (response.ok) {
        const data = await response.json();
        setOptionList(data.map(org => ({
          value: org.organizationCode,
          label: org.organizationName,
          organizationCode: org.organizationCode,
          organizationName: org.organizationName,
          membership: membershipOf(org.organizationCode)
        })));
      } else {
        setOptionList([]);
      }
    } catch {
      setOptionList([]);
    } finally {
      setIsSearching(false);
    }
  };

  const CustomOption = ({ innerRef, innerProps, data, isSelected, isFocused }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: '10px 14px',
        backgroundColor: isSelected ? PURPLE : isFocused ? hexToRgba(PURPLE, .06) : 'white',
        color: isSelected ? 'white' : '#1f1d2e',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ fontWeight: 500, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.organizationName}</span>
        {data.membership && (
          <span style={{
            flexShrink: 0, fontSize: 10.5, fontWeight: 700, lineHeight: 1.5,
            padding: '2px 7px', borderRadius: 999,
            color: isSelected ? 'white' : (data.membership === 'joined' ? MINT : '#d97757'),
            background: isSelected ? 'rgba(255,255,255,.22)'
              : (data.membership === 'joined' ? hexToRgba(MINT, .14) : 'rgba(217,119,87,.14)'),
          }}>{data.membership === 'joined' ? '가입됨' : '신청됨'}</span>
        )}
      </span>
      <span style={{
        color: isSelected ? 'rgba(255,255,255,0.75)' : '#8b8a99',
        fontSize: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontWeight: 400,
        flexShrink: 0,
      }}>
        {data.organizationCode}
      </span>
    </div>
  );

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: 10,
      border: state.isFocused ? `1.5px solid ${PURPLE}` : '1.5px solid #ececf2',
      boxShadow: state.isFocused ? `0 0 0 4px ${hexToRgba(PURPLE, .10)}` : 'none',
      background: 'white',
      padding: '2px 4px',
      minHeight: 44,
      transition: 'border-color 140ms, box-shadow 140ms',
      '&:hover': { borderColor: PURPLE }
    }),
    placeholder: (base) => ({ ...base, color: '#aaa9b7', fontSize: 13.5 }),
    input: (base) => ({ ...base, fontSize: 13.5, color: '#1f1d2e' }),
    singleValue: (base) => ({ ...base, fontSize: 13.5, color: '#1f1d2e' }),
    menu: (base) => ({
      ...base,
      borderRadius: 10,
      boxShadow: '0 8px 24px -8px rgba(60,50,120,.18)',
      border: '1px solid #ececf2',
      overflow: 'hidden',
    }),
    menuList: (base) => ({ ...base, padding: 4 }),
    noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: '#8b8a99', padding: '12px 14px' }),
    loadingMessage: (base) => ({ ...base, fontSize: 13, color: '#8b8a99' }),
  };

  return (
    <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
      <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>
        <OrgSectionTabs active="org-join" />

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: 16 }}>
          {/* 검색 카드 */}
          <div style={{
            background: 'white', borderRadius: 18,
            border: '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 22px',
              background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
              borderBottom: '1px solid #ececf2',
            }}>
              <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>조직 검색</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#8b8a99' }}>
                조직명 또는 조직 코드로 검색하여 가입 신청하세요. 승인은 MASTER가 처리합니다.
              </p>
            </div>

            <div style={{ padding: '28px 22px' }}>
              <Select
                id="organizationCode"
                options={optionList}
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                onInputChange={handleInputValueChange}
                inputValue={inputValue}
                filterOption={() => true}
                placeholder="조직명 또는 조직 코드를 입력하세요"
                noOptionsMessage={() => {
                  if (isSearching) return "검색 중...";
                  if (inputValue.length < 1) return "조직명 또는 조직 코드를 입력해주세요.";
                  return "검색 결과가 없습니다.";
                }}
                isClearable
                isSearchable
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => {
                  if (inputValue.length >= 1 && optionList.length > 0) setMenuIsOpen(true);
                }}
                onMenuClose={() => setMenuIsOpen(false)}
                styles={selectStyles}
                components={{ Option: CustomOption, DropdownIndicator: () => null }}
              />

              <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: '#fafaff', border: '1px solid #ececf2' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8b8a99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#5d5b6e' }}>검색 방법</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#8b8a99', lineHeight: 1.6 }}>
                  조직명 일부 또는 조직 코드(예: <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#5d5b6e', fontSize: 11.5 }}>QG384B</code>)를
                  입력하면 일치하는 조직 목록이 표시됩니다.
                </p>
              </div>
            </div>
          </div>

          {/* 안내 패널 */}
          <div style={{
            background: 'white', borderRadius: 18,
            border: '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '16px 18px', borderBottom: '1px solid #ececf2',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 7,
                background: hexToRgba(MINT, .15), color: MINT,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1f1d2e' }}>가입 안내</h3>
            </div>

            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { step: '1', title: '조직 검색', desc: '조직명 또는 조직 코드로 가입할 조직을 검색하세요.' },
                { step: '2', title: '가입 신청', desc: '목록에서 조직을 선택하면 가입 신청이 접수됩니다.' },
                { step: '3', title: 'MASTER 승인 대기', desc: '관리자가 승인하면 조직 구성원이 됩니다.' },
              ].map(({ step, title, desc }) => (
                <div key={step} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 999, flexShrink: 0,
                    background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                    color: 'white', fontSize: 11, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 1,
                  }}>{step}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f1d2e' }}>{title}</div>
                    <div style={{ fontSize: 12, color: '#8b8a99', marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              ))}

              <div style={{
                marginTop: 4, borderRadius: 10, padding: '12px 14px',
                background: `linear-gradient(135deg, ${hexToRgba(PURPLE, .06)}, ${hexToRgba(MINT, .05)})`,
                border: `1px dashed ${hexToRgba(PURPLE, .20)}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill={PURPLE}>
                    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                  </svg>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: PURPLE, letterSpacing: '.04em' }}>TIP</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#3a3849', lineHeight: 1.55 }}>
                  조직 코드를 알고 있다면 코드를 직접 입력해 빠르게 찾을 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
